import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { errorText } from "@/components/ErrorPanel";
import { ROUTES } from "../links";
import { usePageTitle } from "../pages/usePageTitle";
import { type BlogPost, usePost, usePublishedPosts } from "./api";
import { authorInitials, coverGradient, formatDate, readTime } from "./format";
import { renderMarkdown } from "./markdown";

/** Loading and error states on the dark site (the app's wp-* panels assume a light page). */
function Status({ error, onRetry }: { error?: unknown; onRetry?: () => void }) {
  if (!error) {
    return (
      <div className="bempty" role="status" aria-live="polite" data-testid="loading">
        <p>Loading&hellip;</p>
      </div>
    );
  }
  return (
    <div className="bempty" role="alert" data-testid="error-panel">
      <h2>We couldn&rsquo;t load this</h2>
      <p>{errorText(error)}</p>
      {onRetry && (
        <button type="button" className="sbtn" style={{ marginTop: 18 }} onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

function Meta({ post, draft }: { post: BlogPost; draft?: boolean }) {
  return (
    <div className="bmeta">
      <span className="bav" aria-hidden="true">
        {authorInitials(post.authorName)}
      </span>
      <span>{post.authorName}</span>
      <span className="bdot" />
      <span>{draft && !post.published ? "Unpublished" : formatDate(post.publishedAt)}</span>
      <span className="bdot" />
      <span>{readTime(post.contentMd)} min read</span>
    </div>
  );
}

function Cover({ post }: { post: BlogPost }) {
  return (
    <div className="bcover" style={{ background: coverGradient(post) }}>
      {post.coverImageUrl ? (
        <img src={post.coverImageUrl} alt="" loading="lazy" />
      ) : (
        <span className="bletter" aria-hidden="true">
          {post.category?.[0] || "·"}
        </span>
      )}
      {post.category && <span className="btag">{post.category}</span>}
    </div>
  );
}

export function PostCard({ post, featured }: { post: BlogPost; featured?: boolean }) {
  const H = featured ? "h2" : "h3";
  return (
    <Link className={`bcard${featured ? " feat" : ""}`} to={`${ROUTES.blog}/${post.slug}`}>
      <Cover post={post} />
      <div className="bbody">
        <H>{post.title}</H>
        {post.excerpt && <p>{post.excerpt}</p>}
        <Meta post={post} />
      </div>
    </Link>
  );
}

/** /blog: the WealthyPlanet Journal, newest first, filterable by category. */
export function BlogListPage() {
  usePageTitle("Blog");
  const query = usePublishedPosts();
  const [category, setCategory] = useState("All");
  const posts = useMemo(() => query.data ?? [], [query.data]);
  const categories = useMemo(
    () => ["All", ...new Set(posts.map((p) => p.category).filter(Boolean))],
    [posts],
  );
  const visible = category === "All" ? posts : posts.filter((p) => p.category === category);
  const [featured, ...rest] = visible;

  return (
    <div className="spage on" data-s="blog">
      <header className="hero" style={{ paddingBottom: 0, textAlign: "left" }}>
        <div className="wrap">
          <p className="mono">The WealthyPlanet Journal</p>
          <h1 style={{ marginLeft: 0, marginTop: 14 }}>
            Money, made <em>legible.</em>
          </h1>
          <p className="lede" style={{ marginLeft: 0 }}>
            Field notes on financial planning, behavioural money habits, and how Abi turns big life
            decisions into a clear plan. Written by the WealthyPlanet team.
          </p>
        </div>
      </header>
      <section className="band tight">
        <div className="wrap">
          {query.isPending ? (
            <Status />
          ) : query.isError ? (
            <Status error={query.error} onRetry={() => query.refetch()} />
          ) : posts.length === 0 ? (
            <div className="bempty">
              <h2>Nothing here yet.</h2>
              <p>New writing from the WealthyPlanet team is on the way.</p>
            </div>
          ) : (
            <>
              {categories.length > 2 && (
                <div className="bcats" role="group" aria-label="Filter by category">
                  {categories.map((c) => (
                    <button
                      type="button"
                      key={c}
                      className="bcat"
                      aria-pressed={category === c}
                      onClick={() => setCategory(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              {featured && <PostCard post={featured} featured />}
              {rest.length > 0 && (
                <>
                  <h2 style={{ color: "#fff", fontSize: 28, margin: "40px 0 20px" }}>
                    Latest writing
                  </h2>
                  <div className="bgrid">
                    {rest.map((p) => (
                      <PostCard key={p.slug} post={p} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/** /blog/:slug */
export function BlogPostPage() {
  const { slug = "" } = useParams();
  const query = usePost(slug);
  const post = query.data;
  usePageTitle(post ? post.title : "Blog");
  const html = useMemo(() => renderMarkdown(post?.contentMd), [post?.contentMd]);

  if (query.isPending || query.isError || !post) {
    return (
      <div className="spage on" data-s="blog">
        <section className="band">
          <div className="wrap" style={{ maxWidth: 760 }}>
            {query.isPending ? (
              <Status />
            ) : query.isError ? (
              <Status error={query.error} onRetry={() => query.refetch()} />
            ) : (
              <div className="bempty" data-testid="post-not-found">
                <h2>Post not found</h2>
                <p>This post may have been unpublished or moved.</p>
                <Link className="sbtn p" style={{ marginTop: 22 }} to={ROUTES.blog}>
                  Back to the Journal
                </Link>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="spage on" data-s="blog">
      <div className="bposthero" style={{ background: coverGradient(post) }}>
        {post.coverImageUrl && <img src={post.coverImageUrl} alt="" />}
      </div>
      <section className="band tight">
        <article className="wrap" style={{ maxWidth: 760 }}>
          <Link className="bback" to={ROUTES.blog}>
            &larr; All posts
          </Link>
          {post.category && (
            <p className="mono" style={{ marginTop: 22 }}>
              {post.category}
            </p>
          )}
          <h1 className="bposth1">{post.title}</h1>
          <div className="bpostmeta">
            <Meta post={post} />
          </div>
          <div
            className="sprose"
            style={{ margin: 0 }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </article>
      </section>
    </div>
  );
}
